import type { OrganizationMembership } from "@fleetminder/contracts";
import { Spinner } from "@fleetminder/ui";

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { createContext, type PropsWithChildren, use, useMemo } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

import { apiRequest, WebApiError } from "./api-client.js";
import { fetchOrganizations, signOut } from "./auth-client.js";

/**
 * The session lives in the HTTP-only cookie the API sets; the browser can
 * never read it. This module asks the API who the caller is, caches that
 * answer, and turns "no session" into a redirect rather than a retry loop.
 */

export type SessionUser = {
  readonly email: string;
  readonly emailVerified: boolean;
  readonly id: string;
  readonly image: string | null;
  readonly name: string;
};

type GetSessionResponse = {
  readonly session?: { readonly expiresAt?: string };
  readonly user?: {
    readonly email?: string;
    readonly emailVerified?: boolean;
    readonly id?: string;
    readonly image?: string | null;
    readonly name?: string;
  };
} | null;

export const sessionQueryKey = ["session"] as const;
export const sessionOrganizationsQueryKey = [
  "session",
  "organizations",
] as const;

/**
 * Better Auth answers an unauthenticated `get-session` with `null` and a 200,
 * so a missing user is a normal result rather than an error. Anything that
 * does throw is a transport failure, and retrying it forever would spin.
 */
async function fetchSession(): Promise<SessionUser | null> {
  const body = await apiRequest<GetSessionResponse>("/auth/get-session");

  if (!body?.user?.id || !body.user.email) {
    return null;
  }

  return {
    email: body.user.email,
    emailVerified: body.user.emailVerified !== false,
    id: body.user.id,
    image: body.user.image ?? null,
    name: body.user.name ?? body.user.email,
  };
}

type SessionContextValue = {
  readonly isLoading: boolean;
  readonly organizations: readonly OrganizationMembership[];
  readonly refresh: () => Promise<unknown>;
  readonly signOut: () => Promise<void>;
  readonly status: "authenticated" | "signed-out" | "unavailable";
  readonly user: SessionUser | null;
};

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined,
);

export function SessionProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();

  const session: UseQueryResult<SessionUser | null> = useQuery({
    queryFn: fetchSession,
    queryKey: sessionQueryKey,
    // One attempt. A failed session fetch must not become a request loop.
    retry: false,
    staleTime: 60_000,
  });

  // Memberships are part of "who is signed in": every organization-scoped
  // route needs them to decide whether a deep link is even permitted.
  const organizations = useQuery({
    enabled: Boolean(session.data),
    queryFn: () => fetchOrganizations(),
    queryKey: sessionOrganizationsQueryKey,
    retry: false,
    staleTime: 60_000,
  });

  const { mutateAsync: endSession } = useMutation({
    mutationFn: async () => {
      await signOut();
    },
    onSettled: () => {
      // Clearing rather than invalidating: nothing from the previous session
      // may be served, even briefly, to whoever signs in next.
      queryClient.clear();
    },
  });

  const value = useMemo<SessionContextValue>(() => {
    const status: SessionContextValue["status"] = session.isError
      ? "unavailable"
      : session.data
        ? "authenticated"
        : "signed-out";

    return {
      // Both queries, not just the session. Memberships decide where a
      // signed-in person lands, and treating "not loaded yet" as "belongs to
      // nothing" sent someone with an organization to the page that offers to
      // create one. The organizations query is disabled until there is a
      // session, and a disabled query reports itself as pending forever, so
      // it only counts once it has actually been enabled.
      isLoading:
        session.isPending || (Boolean(session.data) && organizations.isPending),
      organizations: organizations.data ?? [],
      refresh: () => session.refetch(),
      signOut: async () => {
        await endSession();
      },
      status,
      user: session.data ?? null,
    };
  }, [endSession, organizations.data, organizations.isPending, session]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionContextValue {
  const context = use(SessionContext);

  if (!context) {
    throw new Error("useSession must be used inside a SessionProvider.");
  }

  return context;
}

/**
 * Builds the sign-in path that returns the visitor to where they were headed.
 * Only same-origin application paths survive, so a crafted link cannot turn
 * the sign-in redirect into an open redirect.
 */
/**
 * The counterpart to RequireSession, for the pages that only make sense when
 * signed out.
 *
 * Without it, someone already signed in who opens the sign-in page is shown
 * the form again and has to work out that they did not need it. The guard
 * existed in one direction only.
 *
 * A returnTo carried on the URL is honoured, so following a link that
 * bounced off RequireSession and then finding an existing session still ends
 * up where the visitor was going.
 */
export function RedirectWhenSignedIn({ children }: PropsWithChildren) {
  const { isLoading, status } = useSession();
  const [searchParams] = useSearchParams();

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center gap-3 text-sm text-muted-foreground"
        role="status"
      >
        <Spinner className="size-5" />
        Loading
      </div>
    );
  }

  if (status !== "authenticated") {
    return children;
  }

  const requested = searchParams.get("returnTo");
  // Only a path within the application: an absolute or protocol-relative
  // value here would be an open redirect, and /auth/ would bounce straight
  // back to this guard.
  const destination =
    requested &&
    requested.startsWith("/") &&
    !requested.startsWith("//") &&
    !requested.startsWith("/auth/") &&
    !requested.includes("\\")
      ? requested
      : "/app";

  return <Navigate replace to={destination} />;
}

export function createSignInPath(returnTo: string): string {
  const safeReturnTo =
    returnTo.startsWith("/") &&
    !returnTo.startsWith("//") &&
    !returnTo.startsWith("/auth/") &&
    !returnTo.includes("\\")
      ? returnTo
      : "/app";

  return `/auth/sign-in?returnTo=${encodeURIComponent(safeReturnTo)}`;
}

export function isAuthorizationFailure(error: unknown): boolean {
  return error instanceof WebApiError && error.category === "authorization";
}

/** Route guard for everything under `/app`. */
export function RequireSession({ children }: PropsWithChildren) {
  const { isLoading, status } = useSession();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center gap-3 text-sm text-muted-foreground"
        role="status"
      >
        <Spinner className="size-5" />
        Signing you in
      </div>
    );
  }

  if (status === "authenticated") {
    return children;
  }

  // A transport failure is reported as signed-out rather than retried: the
  // visitor gets a sign-in page they can act on instead of a stuck spinner.
  return (
    <Navigate
      replace
      to={createSignInPath(`${location.pathname}${location.search}`)}
    />
  );
}
